import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { dispatchApi, type MyQueueItem } from "@/lib/api";
import { formatNaira } from "@/lib/mock-data";
import { useAuth } from "@/stores/auth";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { Check, X, PackageCheck, MapPin } from "lucide-react";

export default function Jobs() {
  const token = useAuth((s) => s.session?.accessToken);
  const [items, setItems] = useState<MyQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    dispatchApi
      .myQueue(token)
      .then(setItems)
      .catch(() => toast.error("Failed to load your jobs"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (parcelId: string, action: "accept" | "decline") => {
    setActing(parcelId);
    try {
      if (action === "accept") await dispatchApi.accept(parcelId, token);
      else await dispatchApi.decline(parcelId, undefined, token);
      toast.success(action === "accept" ? "Accepted" : "Declined");
      setItems((cur) => cur.filter((i) => i.parcel_id !== parcelId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not respond");
    } finally {
      setActing(null);
    }
  };

  return (
    <>
      <Topbar title="My jobs" subtitle="New delivery offers waiting on your response" />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        {loading ? (
          <LoadingBlock label="Loading your jobs…" />
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
            <PackageCheck className="h-8 w-8" />
            No pending offers right now.
          </div>
        ) : (
          items.map((job) => (
            <div key={job.assignment_id} className="surface-card flex items-center justify-between gap-4 p-4">
              <div className="min-w-0 space-y-1">
                <div className="font-mono text-xs text-muted-foreground">{job.tracking_id}</div>
                <div className="flex items-start gap-1.5 text-sm">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                  <span className="truncate">{job.pickup_addr}</span>
                </div>
                <div className="flex items-start gap-1.5 text-sm">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                  <span className="truncate">{job.dropoff_addr}</span>
                </div>
                <div className="text-sm font-semibold">{formatNaira(job.amount_kobo / 100)}</div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" disabled={acting === job.parcel_id} onClick={() => respond(job.parcel_id, "accept")}>
                  <Check className="mr-1.5 h-4 w-4" /> Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  disabled={acting === job.parcel_id}
                  onClick={() => respond(job.parcel_id, "decline")}
                >
                  <X className="mr-1.5 h-4 w-4" /> Decline
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
